import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { PremiumCarCard } from '../components/PremiumCarCard';
import { FamiliesCarousel } from '../components/FamiliesCarousel';
import { 
  Search, Shield, FileCheck, Truck, CreditCard, 
  CheckCircle2, Award, Clock, HeadphonesIcon, TrendingUp,
  ChevronRight, Star, MapPin, Heart, Flame
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;
const API = `${BACKEND_URL}/api`;

export const HomePage = () => {
  const [featuredCars, setFeaturedCars] = useState([]);
  const [hotDeals, setHotDeals] = useState([]);
  const [tiles, setTiles] = useState([]);
  const [recentlySoldCars, setRecentlySoldCars] = useState([]);
  const [deliveries, setDeliveries] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const fetchFeaturedCars = useCallback(async () => {
    let hasShownCachedData = false;
    
    try {
      // Step 1: Show prefetched data instantly
      if (window.__PREFETCH__?.featuredData) {
        console.log('[HomePage] Showing prefetched featured cars instantly');
        const data = window.__PREFETCH__.featuredData;
        const cars = Array.isArray(data) ? data : (data.cars || []);
        setFeaturedCars(cars.slice(0, 6));
        setLoading(false);
        hasShownCachedData = true;
      } else if (window.__PREFETCH__?.featured) {
        try {
          const data = await window.__PREFETCH__.featured;
          if (data) {
            const cars = Array.isArray(data) ? data : (data.cars || []);
            setFeaturedCars(cars.slice(0, 6));
            setLoading(false);
            hasShownCachedData = true;
          }
        } catch (e) {
          // Prefetch failed
        }
      }
      
      // Step 2: Always fetch fresh data in background
      console.log('[HomePage] Fetching fresh featured cars...');
      const response = await axios.get(`${API}/cars?featured=true&limit=6`);
      const freshCars = Array.isArray(response.data) ? response.data : (response.data.cars || []);
      setFeaturedCars(freshCars.slice(0, 6));
      
      // Update prefetch cache
      window.__PREFETCH__ = window.__PREFETCH__ || {};
      window.__PREFETCH__.featuredData = { cars: freshCars };
      
    } catch (error) {
      console.error('Error fetching featured cars:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchRecentlySoldCars = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/cars/recently-sold?limit=4`);
      setRecentlySoldCars(response.data);
    } catch (error) {
      console.error('Error fetching recently sold cars:', error);
    }
  }, []);

  const fetchDeliveries = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/delivery-images?limit=6`);
      setDeliveries(response.data);
    } catch (error) {
      console.error('Error fetching deliveries:', error);
    }
  }, []);

  const fetchTestimonials = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/testimonials?active_only=true`);
      setTestimonials(response.data.slice(0, 3));
    } catch (error) {
      console.error('Error fetching testimonials:', error);
    }
  }, []);

  const fetchHotDeals = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/cars/hot-deals?limit=6`);
      setHotDeals(response.data);
    } catch (error) {
      console.error('Error fetching hot deals:', error);
    }
  }, []);

  const fetchTiles = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/tiles`);
      setTiles(response.data);
    } catch (error) {
      console.error('Error fetching tiles:', error);
    }
  }, []);

  useEffect(() => {
    fetchFeaturedCars();
    fetchHotDeals();
    fetchTiles();
    fetchRecentlySoldCars();
    fetchDeliveries();
    fetchTestimonials();
  }, [fetchFeaturedCars, fetchHotDeals, fetchTiles, fetchRecentlySoldCars, fetchDeliveries, fetchTestimonials]);

  const trustBadges = [
    { icon: Shield, title: 'Verified Cars', description: 'Every car inspected by experts' },
    { icon: FileCheck, title: 'No Hidden Charges', description: 'Transparent pricing guaranteed' },
    { icon: CreditCard, title: 'RC Transfer Support', description: 'Complete documentation help' },
    { icon: Award, title: 'Loan Assistance', description: 'Easy financing options' },
    { icon: Truck, title: 'Home Delivery', description: 'Doorstep delivery available' }
  ];

  const whyBuyFromUs = [
    { icon: CheckCircle2, title: 'Carefully Selected', description: 'Handpicked quality vehicles' },
    { icon: Shield, title: 'Verified Docs', description: 'All papers checked' },
    { icon: TrendingUp, title: 'Fair Pricing', description: 'Best market rates' },
    { icon: Clock, title: 'Quick Delivery', description: 'Fast processing' },
    { icon: HeadphonesIcon, title: 'After Sales', description: '24/7 support' }
  ];

  const budgetCategories = [
    { label: 'Under ₹2 Lakh', image: 'https://images.unsplash.com/photo-1583121274602-3e2820c69888?w=400', filter: { priceRange: '0-200000' } },
    { label: 'Under ₹3 Lakh', image: 'https://images.unsplash.com/photo-1552519507-da3b142c6e3d?w=400', filter: { priceRange: '0-300000' } },
    { label: 'Under ₹5 Lakh', image: 'https://images.unsplash.com/photo-1549317661-bd32c8ce0db2?w=400', filter: { priceRange: '0-500000' } },
    { label: 'SUV Under Budget', image: 'https://images.unsplash.com/photo-1605559424843-9e4c228bf1c2?w=400', filter: { bodyType: 'SUV' } },
    { label: 'Automatic Cars', image: 'https://images.unsplash.com/photo-1617531653520-bd466115490d?w=400', filter: { transmission: 'Automatic' } }
  ];

  // Quick filter chips mapping
  const quickFilters = [
    { label: 'Under ₹3 Lakh', filter: { priceRange: '0-300000' } },
    { label: 'SUV', filter: { bodyType: 'SUV' } },
    { label: 'Automatic', filter: { transmission: 'Automatic' } },
    { label: 'First Owner', filter: { ownership: '1' } },
  ];

  // Search form state
  const [searchBudget, setSearchBudget] = useState('');
  const [searchBrand, setSearchBrand] = useState('');
  const [searchType, setSearchType] = useState('');

  // Handle filter navigation (for quick filters and tiles)
  const handleFilterClick = (category) => {
    const params = new URLSearchParams();
    if (category.filter.priceRange) params.set('priceRange', category.filter.priceRange);
    if (category.filter.transmission) params.set('transmission', category.filter.transmission);
    if (category.filter.bodyType) params.set('bodyType', category.filter.bodyType);
    if (category.filter.fuelType) params.set('fuelType', category.filter.fuelType);
    if (category.filter.ownership) params.set('ownership', category.filter.ownership);
    if (category.filter.brand) params.set('brand', category.filter.brand);
    navigate(`/inventory?${params.toString()}`);
  };

  // Handle tile click - convert tile to filter format
  const handleTileClick = (tile) => {
    const params = new URLSearchParams();
    
    // Map tile filter_type to URL parameter
    switch (tile.filter_type) {
      case 'budget':
        params.set('priceRange', `0-${tile.filter_value}`);
        break;
      case 'bodyType':
        params.set('bodyType', tile.filter_value);
        break;
      case 'transmission':
        params.set('transmission', tile.filter_value);
        break;
      case 'fuelType':
        params.set('fuelType', tile.filter_value);
        break;
      case 'ownership':
        params.set('ownership', tile.filter_value);
        break;
      case 'hotDeal':
        params.set('hotDeal', 'true');
        break;
      default:
        break;
    }
    
    navigate(`/inventory?${params.toString()}`);
  };

  // Handle hero search button
  const handleHeroSearch = () => {
    const params = new URLSearchParams();
    if (searchBudget) params.set('priceRange', `0-${searchBudget}`);
    if (searchBrand) params.set('brand', searchBrand);
    if (searchType) params.set('bodyType', searchType);
    navigate(`/inventory?${params.toString()}`);
  };

  const formatPrice = (price) => {
    if (price >= 10000000) return `₹${(price / 10000000).toFixed(2)} Cr`;
    if (price >= 100000) return `₹${(price / 100000).toFixed(2)} L`;
    return `₹${price.toLocaleString('en-IN')}`;
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <section 
        className="relative min-h-screen flex items-center bg-cover bg-center"
        style={{
          backgroundImage: 'linear-gradient(rgba(15, 23, 42, 0.85), rgba(15, 23, 42, 0.75)), url(https://images.unsplash.com/photo-1758311177173-f1ffc7fa09bc?w=1920)'
        }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 w-full">
          <div className="max-w-3xl">
            <h1 className="font-bold text-5xl md:text-6xl lg:text-7xl text-white mb-6 leading-tight">
              Buy Trusted Used Cars
              <br />
              <span className="text-orange-500">Without the Stress</span>
            </h1>
            <p className="text-xl md:text-2xl text-gray-200 mb-8">
              Inspected | Verified | Transparent Pricing | Fast Delivery
            </p>

            <div className="bg-white bg-opacity-10 backdrop-blur-lg border border-white border-opacity-20 rounded-2xl p-6 mb-8">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                <select 
                  value={searchBudget}
                  onChange={(e) => setSearchBudget(e.target.value)}
                  className="px-4 py-3 bg-white rounded-xl text-gray-900 focus:ring-2 focus:ring-orange-500 border-0"
                  data-testid="hero-budget-filter"
                >
                  <option value="">Search by Budget</option>
                  <option value="200000">Under ₹2 Lakh</option>
                  <option value="300000">Under ₹3 Lakh</option>
                  <option value="500000">Under ₹5 Lakh</option>
                  <option value="1000000">Under ₹10 Lakh</option>
                </select>

                <select 
                  value={searchBrand}
                  onChange={(e) => setSearchBrand(e.target.value)}
                  className="px-4 py-3 bg-white rounded-xl text-gray-900 focus:ring-2 focus:ring-orange-500 border-0"
                  data-testid="hero-brand-filter"
                >
                  <option value="">Search by Brand</option>
                  <option value="Maruti Suzuki">Maruti Suzuki</option>
                  <option value="Hyundai">Hyundai</option>
                  <option value="Honda">Honda</option>
                  <option value="Tata">Tata</option>
                  <option value="Mahindra">Mahindra</option>
                  <option value="Toyota">Toyota</option>
                  <option value="Kia">Kia</option>
                  <option value="Skoda">Skoda</option>
                  <option value="Volkswagen">Volkswagen</option>
                  <option value="BMW">BMW</option>
                  <option value="Mercedes-Benz">Mercedes-Benz</option>
                  <option value="Audi">Audi</option>
                </select>

                <select 
                  value={searchType}
                  onChange={(e) => setSearchType(e.target.value)}
                  className="px-4 py-3 bg-white rounded-xl text-gray-900 focus:ring-2 focus:ring-orange-500 border-0"
                  data-testid="hero-type-filter"
                >
                  <option value="">Search by Type</option>
                  <option value="Hatchback">Hatchback</option>
                  <option value="Sedan">Sedan</option>
                  <option value="SUV">SUV</option>
                  <option value="MUV">MUV</option>
                </select>
              </div>
              <button 
                onClick={handleHeroSearch}
                className="w-full bg-orange-500 hover:bg-orange-600 text-white px-8 py-4 rounded-full font-semibold text-lg transition-all duration-200 shadow-lg hover:shadow-xl flex items-center justify-center space-x-2"
                data-testid="hero-search-button"
              >
                <Search className="h-5 w-5" />
                <span>Search Cars</span>
              </button>
            </div>

            <div className="flex flex-wrap gap-3">
              {quickFilters.map((chip) => (
                <button
                  key={chip.label}
                  onClick={() => handleFilterClick(chip)}
                  className="px-5 py-2 bg-white bg-opacity-20 backdrop-blur-sm text-white rounded-full text-sm hover:bg-opacity-30 transition-colors border border-white border-opacity-30"
                  data-testid={`quick-filter-${chip.label.replace(/[^a-zA-Z0-9]/g, '-')}`}
                >
                  {chip.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Trust Badge Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-6">
            {trustBadges.map((badge) => {
              const Icon = badge.icon;
              return (
                <div 
                  key={badge.title}
                  className="bg-green-50 rounded-2xl p-6 text-center group hover:shadow-xl transition-all duration-300"
                >
                  <div className="w-14 h-14 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform">
                    <Icon className="h-7 w-7 text-white" />
                  </div>
                  <h3 className="font-semibold text-gray-900 text-lg mb-1">
                    {badge.title}
                  </h3>
                  <p className="text-sm text-gray-600">
                    {badge.description}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Hot Deals Section - Above Featured Cars */}
      {hotDeals.length > 0 && (
        <section className="py-20 bg-gradient-to-b from-orange-50 to-white" data-testid="hot-deals-section">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-orange-500 to-red-500 text-white rounded-full text-sm font-bold mb-4">
                <Flame className="h-4 w-4" />
                <span>LIMITED TIME OFFERS</span>
                <Flame className="h-4 w-4" />
              </div>
              <h2 className="font-bold text-4xl md:text-5xl text-gray-900 mb-4">
                Hot <span className="text-orange-500">Deals</span> Today
              </h2>
              <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                Don't miss these amazing offers! Grab them before they're gone.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-10">
              {hotDeals.map((car) => (
                <PremiumCarCard key={car.id} car={car} />
              ))}
            </div>

            <div className="text-center">
              <button
                onClick={() => navigate('/inventory?hotDeal=true')}
                className="inline-flex items-center space-x-2 px-8 py-4 bg-gradient-to-r from-orange-500 to-red-500 text-white rounded-full font-semibold hover:from-orange-600 hover:to-red-600 transition-all duration-200 shadow-lg hover:shadow-xl"
                data-testid="view-all-hot-deals-btn"
              >
                <Flame className="h-5 w-5" />
                <span>View All Hot Deals</span>
                <ChevronRight className="h-5 w-5" />
              </button>
            </div>
          </div>
        </section>
      )}

      {/* Featured Cars Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="font-bold text-4xl md:text-5xl text-gray-900 mb-4">
              Featured <span className="text-orange-500">Premium</span> Cars
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Handpicked selection of our finest verified pre-owned vehicles
            </p>
          </div>

          {loading ? (
            <div className="text-center py-12">
              <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-orange-500 border-t-transparent"></div>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-10">
              {featuredCars.map((car) => (
                <PremiumCarCard key={car.id} car={car} />
              ))}
            </div>
          )}

          <div className="text-center">
            <button
              onClick={() => navigate('/inventory')}
              className="inline-flex items-center space-x-2 px-8 py-4 bg-blue-600 text-white rounded-full font-semibold hover:bg-blue-700 transition-all duration-200 shadow-lg hover:shadow-xl"
            >
              <span>View All Cars</span>
              <ChevronRight className="h-5 w-5" />
            </button>
          </div>
        </div>
      </section>

      {/* Recent Deliveries Section - "Families Catered So Far.." */}
      {deliveries.length > 0 && (
        <section className="py-20 bg-white" data-testid="deliveries-section">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="font-bold text-4xl md:text-5xl text-gray-900 mb-4">
                Families Catered <span className="text-orange-500">So Far..</span>
              </h2>
              <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                Happy moments from our recent deliveries
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {deliveries.map((delivery) => (
                <div 
                  key={delivery.id}
                  className="group bg-white rounded-2xl overflow-hidden shadow-md hover:shadow-xl transition-all duration-300 hover:-translate-y-1 border border-gray-100"
                  data-testid={`delivery-card-${delivery.id}`}
                >
                  <div className="aspect-[4/3] overflow-hidden relative">
                    <img
                      src={delivery.image_url}
                      alt={`${delivery.customer_name} - ${delivery.car_name}`}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      loading="lazy"
                    />
                    <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-4">
                      <div className="flex items-center space-x-2 text-white">
                        <Heart className="h-4 w-4 text-red-500 fill-red-500" />
                        <span className="font-dmsans text-sm">Happy Customer</span>
                      </div>
                    </div>
                  </div>
                  <div className="p-4">
                    <h3 className="font-outfit font-semibold text-gray-900 mb-1">
                      {delivery.car_name}
                    </h3>
                    <p className="font-dmsans text-sm text-gray-600 mb-2">
                      Delivered to {delivery.customer_name}
                    </p>
                    <div className="flex items-center text-gray-500 text-sm">
                      <MapPin className="h-4 w-4 mr-1" />
                      <span className="font-dmsans">{delivery.delivery_location}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Recently Sold/Booked Cars Section */}
      {recentlySoldCars.length > 0 && (
        <section className="py-20 bg-gray-50" data-testid="recently-sold-section">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="font-bold text-4xl md:text-5xl text-gray-900 mb-4">
                Recently <span className="text-orange-500">Sold</span> Cars
              </h2>
              <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                These cars found their new homes. More coming soon!
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {recentlySoldCars.map((car) => (
                <div 
                  key={car.id}
                  className="group bg-white rounded-2xl overflow-hidden shadow-md border border-gray-100 opacity-80"
                  data-testid={`sold-car-${car.id}`}
                >
                  <div className="aspect-[4/3] overflow-hidden relative">
                    <img
                      src={car.image}
                      alt={`${car.make} ${car.model}`}
                      className="w-full h-full object-cover grayscale"
                      loading="lazy"
                    />
                    {/* Status Badge */}
                    <div className={`absolute top-4 left-4 px-4 py-2 rounded-lg font-bold text-sm text-white shadow-lg ${
                      car.status === 'Sold' ? 'bg-red-600' : 'bg-yellow-500'
                    }`}>
                      {car.status.toUpperCase()}
                    </div>
                  </div>
                  <div className="p-4">
                    <h3 className="font-outfit font-semibold text-gray-900 mb-1 truncate">
                      {car.year} {car.make} {car.model}
                    </h3>
                    <p className="font-outfit font-bold text-xl text-gray-500 line-through">
                      {formatPrice(car.price)}
                    </p>
                    <button
                      disabled
                      className="w-full mt-3 py-2 bg-gray-200 text-gray-500 rounded-full font-dmsans font-medium cursor-not-allowed"
                    >
                      {car.status === 'Sold' ? 'SOLD OUT' : 'BOOKED'}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Why Buy From Us */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="font-bold text-4xl md:text-5xl text-gray-900 mb-4">
              Why Buy From <span className="text-orange-500">TruVant</span>
            </h2>
            <p className="text-lg text-gray-600">
              Experience the difference with our premium service
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-6">
            {whyBuyFromUs.map((item) => {
              const Icon = item.icon;
              return (
                <div 
                  key={item.title}
                  className="bg-gray-50 rounded-2xl p-6 text-center hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
                >
                  <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-3">
                    <Icon className="h-6 w-6 text-orange-500" />
                  </div>
                  <h3 className="font-semibold text-gray-900 mb-1">
                    {item.title}
                  </h3>
                  <p className="text-xs text-gray-600">
                    {item.description}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Budget-Based Discovery - Dynamic Tiles or Fallback */}
      <section className="py-20 bg-gray-50" data-testid="tiles-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="font-bold text-4xl md:text-5xl text-gray-900 mb-4">
              Find Your <span className="text-orange-500">Perfect Match</span>
            </h2>
            <p className="text-lg text-gray-600">
              Browse by your budget and preferences
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
            {/* Use dynamic tiles if available, otherwise fallback to static categories */}
            {(tiles.length > 0 ? tiles : budgetCategories.map((cat, idx) => ({
              id: `static-${idx}`,
              title: cat.label,
              image_url: cat.image,
              filter_type: cat.filter.priceRange ? 'budget' : (cat.filter.bodyType ? 'bodyType' : 'transmission'),
              filter_value: cat.filter.priceRange?.split('-')[1] || cat.filter.bodyType || cat.filter.transmission
            }))).map((tile) => (
              <button
                key={tile.id}
                onClick={() => handleTileClick(tile)}
                className="group relative aspect-square rounded-2xl overflow-hidden shadow-md hover:shadow-xl transition-all duration-300"
                data-testid={`tile-${tile.id}`}
              >
                <img 
                  src={tile.image_url || 'https://images.unsplash.com/photo-1494976388531-d1058494cdd8?w=400'} 
                  alt={tile.title}
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  loading="lazy"
                  onError={(e) => {
                    e.target.src = 'https://images.unsplash.com/photo-1494976388531-d1058494cdd8?w=400';
                  }}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-gray-900 via-gray-900/50 to-transparent flex items-end">
                  <p className="font-semibold text-white p-4 text-center w-full">
                    {tile.title}
                  </p>
                </div>
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      {testimonials.length > 0 && (
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="font-bold text-4xl md:text-5xl text-gray-900 mb-4">
                Happy <span className="text-orange-500">Customers</span>
              </h2>
              <p className="text-lg text-gray-600">
                Real experiences from our satisfied customers
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {testimonials.map((testimonial) => (
                <div key={testimonial.id} className="bg-gray-50 rounded-2xl overflow-hidden shadow-md hover:shadow-xl transition-shadow duration-300">
                  <div className="aspect-video">
                    <iframe
                      width="100%"
                      height="100%"
                      src={`https://www.youtube.com/embed/${testimonial.video_id}`}
                      title={testimonial.customer_name}
                      frameBorder="0"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                      className="w-full h-full"
                    ></iframe>
                  </div>
                  <div className="p-5">
                    <div className="flex items-center space-x-1 mb-2">
                      {[1,2,3,4,5].map((star) => (
                        <Star key={star} className="h-4 w-4 fill-orange-500 text-orange-500" />
                      ))}
                    </div>
                    <p className="font-semibold text-gray-900">
                      {testimonial.customer_name}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Families Catered Section */}
      <FamiliesCarousel />

      {/* Final CTA */}
      <section className="py-20 bg-blue-900 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-bold text-4xl md:text-5xl mb-6">
            Looking For A Specific Car?
          </h2>
          <p className="text-xl text-gray-200 mb-8">
            Can't find what you're looking for? Tell us your requirements and we'll help you find the perfect car.
          </p>
          <button
            onClick={() => navigate('/contact')}
            className="inline-flex items-center space-x-2 px-10 py-5 bg-orange-500 text-white rounded-full font-bold text-lg hover:bg-orange-600 transition-all duration-200 shadow-lg hover:shadow-xl hover:-translate-y-1"
          >
            <span>Tell Us Your Requirement</span>
            <ChevronRight className="h-6 w-6" />
          </button>
        </div>
      </section>
    </div>
  );
};
